import React, { useEffect, useState, FC } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { StackScreen } from "@life-line-lab/components-react";
import { Helmet } from "react-helmet";
import { isNil, isEmpty, map, filter } from "lodash";
import OrderCreateForm from "Components/order-management/order-create-form";

//HOOKS
import useQuery from "Hooks/query.hook";
import { useSession } from "Hooks/index";
import { useItemsPerPage } from "Hooks/index";

// TYPES
import { Sorting, SortingMode } from "../../../../types/common.types";

//CONSTANTS
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";

// HELPERS
import { decode, encode } from "Helpers/enc";
import useOrderFormEdit from "../use-order-form-edit";
import { toNum } from "Helpers/get-to-num";
import useOrderFormStates, {
  OrderFormProvider,
} from "../../../../components/order-management/order-create-form/order-form-states";
import useCorporateType from "Hooks/corporate-type.hook";
import { ParcelStatusByPage, ParcelTypeEnums } from "Data/order";

const defaultSorting = { sortBy: "orgName", mode: SortingMode.DESC };

const OrderEditInner: FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();
  const { id } = useParams<{ id: string }>();
  const { org, roles, roleTypes } = useSession();
  const { isCustomer } = useCorporateType();

  const page = query.get("page") || 1;

  const [itemPerPage, setItemPerPage] = useItemsPerPage(
    DEFAULT_MAX_PER_PAGE.toString(),
  );
  const initialQueryParams = {
    page,
    offset: (toNum(page) - 1) * toNum(itemPerPage),
  };

  const [queryParams, setQueryParams] = useState(initialQueryParams);
  const [sorting, setSorting] = useState<Sorting>(defaultSorting);

  const decodedOrderID = decode(id);

  const {
    handleUpdateOrder,
    handleCalculateUpdatedOrder,
    handleFetchCustomerList,
    handleFetchCustomerLocationList,
    handleGoodsSpecialCareConditionList,
    handleFetchOrderDetails,
    handleFetchDeliveryServiceList,
    handleCloseStackScreen,
    handleCoordinatesChecking,
    handleFetchEstimatedDeliveryDays
  } = useOrderFormEdit({
    decodedOrderID,
    sorting,
    queryParams,
    itemPerPage,
  });

  const { orderEditDetail } = useOrderFormStates();
  const [isSplitShipmentEdit, setIsSplitShipmentEdit] = useState<boolean>(false)

  useEffect(() => {
    if (decodedOrderID) {
      handleFetchOrderDetails(decodedOrderID, true);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(orderEditDetail)) {
      handleFetchDeliveryServiceList(
        orderEditDetail.customer,
        orderEditDetail.recipientAddress.postcode,
      );
      handleGoodsSpecialCareConditionList(orderEditDetail.customer);
      handleFetchCustomerLocationList(orderEditDetail.customer);
      handleFetchCustomerList(orderEditDetail.senderType);
      handleFetchEstimatedDeliveryDays(orderEditDetail.customer, orderEditDetail.recipientAddress.postcode)

      const isDisplayAllSteps = [
        ...ParcelStatusByPage.DIOLKO_PICKUP,
        ...ParcelStatusByPage.CUSTOMER_CONFIRMATION,
        ...ParcelStatusByPage.DIOLKO_REVIEW,
      ].includes(orderEditDetail.orderStatusInfo.orderStatus);

      setIsSplitShipmentEdit(
        orderEditDetail.orderType === ParcelTypeEnums.SPLIT &&
          !isDisplayAllSteps,
      );
    }
  }, [orderEditDetail]);

  const orderFunctionProps = {
    onSubmit: handleUpdateOrder,
    onSubmitCalculate: handleCalculateUpdatedOrder,
    handleFetchCustomerList,
    onFetchCustomerLocationList: handleFetchCustomerLocationList,
    handleGoodsSpecialCareConditionList,
    onFetchDeliveryServiceList: handleFetchDeliveryServiceList,
    handleCloseStackScreen,
    handleCoordinatesChecking,
    handleFetchEstimatedDeliveryDays,
    isSplitShipmentEdit
  };

  return (
    <StackScreen
      title={
        <div>
          <img
            alt={t("alt.logo")}
            src="/assets/images/banner_logo.png"
            height={30}
          />
        </div>
      }
      isOpen={true}
      renderHeader={<div></div>}
      onClose={handleCloseStackScreen}
    >
      <Helmet>
        <title>{t("pageTitle.editParcelOrder")}</title>
      </Helmet>

      <OrderCreateForm
        {...orderFunctionProps}
        companyDetail={org?.name}
        customerDetails={{}}
        isRoleCustomer={isCustomer}
        isEdit={true}
      />
    </StackScreen>
  );
};

const OrderEdit: FC = () => {
  return (
    <OrderFormProvider>
      <OrderEditInner />
    </OrderFormProvider>
  );
};

export default OrderEdit;
